import { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';

import { cn, POPOVER_MOTION } from '../../shared/lib/cn';
import { Button } from '../../shared/ui/button';
import { Chip } from '../../shared/ui/chip';
import { Segmented } from '../../shared/ui/segmented';
import { ChevronDownIcon, RequestIcon } from '../../shared/ui/icons';
import { clockTime, shortDate, spansDays } from '../../shared/lib/format';
import type { IEventFilters } from '../../entities/event';

// There is deliberately no "Clear": the API refuses a query with no window, so Reset goes to 24 hours.
const UNIT_ROWS: { label: string; minutes: number; steps: number[] }[] = [
  { label: 'Minutes', minutes: 1, steps: [5, 15, 30, 45] },
  { label: 'Hours', minutes: 60, steps: [1, 3, 6, 12, 24] },
  { label: 'Days', minutes: 60 * 24, steps: [2, 7, 14, 30] },
  { label: 'Months', minutes: 60 * 24 * 30, steps: [3, 6, 12] },
];

const DEFAULT_MINUTES = 24 * 60;
const TABS = [
  { value: 'relative' as const, label: 'Relative' },
  { value: 'absolute' as const, label: 'Absolute' },
];
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
const addMonths = (date: Date, count: number) =>
  new Date(date.getFullYear(), date.getMonth() + count, 1);

const monthCells = (month: Date): (Date | null)[] => {
  const first = startOfMonth(month);
  const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  return [
    ...Array.from({ length: first.getDay() }, () => null),
    ...Array.from(
      { length: days },
      (_, i) => new Date(month.getFullYear(), month.getMonth(), i + 1),
    ),
  ];
};

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const monthName = (date: Date) =>
  new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(date);

const dateField = (date: Date) =>
  `${date.getFullYear()}/${`${date.getMonth() + 1}`.padStart(2, '0')}/${`${date.getDate()}`.padStart(2, '0')}`;

const timeField = (date: Date) =>
  [date.getHours(), date.getMinutes(), date.getSeconds()]
    .map((part) => `${part}`.padStart(2, '0'))
    .join(':');

const parseInstant = (date: string, time: string): Date | null => {
  const [y, m, d] = date.split(/[/\-.]/).map(Number);
  const [hh = 0, mm = 0, ss = 0] = time.split(':').map(Number);
  if (!y || !m || !d) return null;
  const parsed = new Date(y, m - 1, d, hh, mm, ss);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const Calendar = ({
  month,
  from,
  to,
  onPick,
}: {
  month: Date;
  from: Date | null;
  to: Date | null;
  onPick: (date: Date) => void;
}) => (
  <div>
    <p className="mb-2 text-center text-meta font-semibold text-primary">{monthName(month)}</p>
    <div className="grid grid-cols-7 gap-px">
      {WEEKDAYS.map((day, index) => (
        <span
          key={index}
          className="flex h-5 w-cell items-center justify-center font-mono text-caps font-medium text-faint"
        >
          {day}
        </span>
      ))}
      {monthCells(month).map((day, index) => {
        if (!day) return <span key={index} className="h-cell-h w-cell" />;
        const isEnd = (from && sameDay(day, from)) || (to && sameDay(day, to));
        const inside = from && to && day > from && day < to;
        return (
          <button
            key={index}
            type="button"
            onClick={() => onPick(day)}
            className={cn(
              'flex h-cell-h w-cell items-center justify-center font-mono text-meta',
              isEnd
                ? 'rounded-tag bg-accent font-semibold text-surface'
                : inside
                  ? 'bg-highlight text-primary'
                  : 'rounded-tag text-muted hover:bg-hover-raised',
            )}
          >
            {day.getDate()}
          </button>
        );
      })}
    </div>
  </div>
);

export type IPeriodValue = Pick<IEventFilters, 'minutes' | 'from' | 'to'>;

interface IPeriodPickerProps<T extends IPeriodValue> {
  filters: T;
  onChange: (next: T) => void;
  /** The widest window the list's API takes; steps past it are not offered. */
  maxMinutes?: number;
}

export const PeriodPicker = <T extends IPeriodValue>({
  filters,
  onChange,
  maxMinutes,
}: IPeriodPickerProps<T>) => {
  const [open, setOpen] = useState(false);
  const absolute = Boolean(filters.from);
  const [tab, setTab] = useState<'relative' | 'absolute'>(absolute ? 'absolute' : 'relative');
  const [month, setMonth] = useState(() => startOfMonth(new Date()));

  const initialFrom = filters.from ? new Date(filters.from) : null;
  const initialTo = filters.to ? new Date(filters.to) : null;
  const [from, setFrom] = useState<Date | null>(initialFrom);
  const [to, setTo] = useState<Date | null>(initialTo);
  const [fromDate, setFromDate] = useState(initialFrom ? dateField(initialFrom) : '');
  const [fromTime, setFromTime] = useState(initialFrom ? timeField(initialFrom) : '00:00:00');
  const [toDate, setToDate] = useState(initialTo ? dateField(initialTo) : '');
  const [toTime, setToTime] = useState(initialTo ? timeField(initialTo) : '23:59:59');

  // Within one day the dates say nothing twice; the times are what was picked.
  const label = absolute
    ? filters.to && !spansDays({ from: filters.from!, to: filters.to })
      ? `${shortDate(filters.from!)} · ${clockTime(filters.from!)} – ${clockTime(filters.to)}`
      : `${shortDate(filters.from!)} – ${filters.to ? shortDate(filters.to) : 'now'}`
    : relativeLabel(filters.minutes ?? DEFAULT_MINUTES);

  const pick = (day: Date) => {
    if (!from || (from && to)) {
      setFrom(day);
      setTo(null);
      setFromDate(dateField(day));
      return;
    }
    const [a, b] = day < from ? [day, from] : [from, day];
    setFrom(a);
    setTo(b);
    setFromDate(dateField(a));
    setToDate(dateField(b));
  };

  const start = parseInstant(fromDate, fromTime);
  const end = parseInstant(toDate, toTime);
  const ordered = start && end ? start < end : false;
  const tooWide =
    maxMinutes !== undefined && start && end
      ? end.getTime() - start.getTime() > maxMinutes * 60_000
      : false;
  const rows = UNIT_ROWS.map((row) => ({
    ...row,
    steps: row.steps.filter((step) => maxMinutes === undefined || step * row.minutes <= maxMinutes),
  })).filter((row) => row.steps.length > 0);

  const applyRelative = (minutes: number) => {
    onChange({ ...filters, minutes, from: undefined, to: undefined });
    setOpen(false);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Chip active>
          <RequestIcon size={13} />
          {label}
          <ChevronDownIcon size={13} />
        </Chip>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className={cn(
            'w-period overflow-hidden rounded-card border border-border-raised bg-surface-raised shadow-panel',
            'origin-(--radix-popover-content-transform-origin)',
            POPOVER_MOTION,
          )}
        >
          <div className="border-b border-border-raised p-3">
            <Segmented aria-label="Window" segments={TABS} value={tab} onChange={setTab} />
          </div>

          {tab === 'relative' ? (
            <div className="flex flex-col gap-inset p-4">
              {rows.map((row) => (
                <div key={row.label} className="flex items-center gap-3">
                  <span className="w-preset shrink-0 text-meta text-muted">{row.label}</span>
                  <span className="flex flex-wrap gap-label">
                    {row.steps.map((step) => {
                      const minutes = step * row.minutes;
                      const on = !absolute && (filters.minutes ?? DEFAULT_MINUTES) === minutes;
                      return (
                        <button
                          key={step}
                          type="button"
                          onClick={() => applyRelative(minutes)}
                          className={cn(
                            'h-control-xs min-w-preset rounded-control border px-inset text-meta font-medium',
                            on
                              ? 'border-accent bg-highlight text-primary'
                              : 'border-border bg-field-muted text-muted hover:text-primary',
                          )}
                        >
                          {step}
                        </button>
                      );
                    })}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-4 p-4">
              <div className="flex items-start justify-center gap-5">
                <Calendar month={month} from={from} to={to} onPick={pick} />
                <Calendar month={addMonths(month, 1)} from={from} to={to} onPick={pick} />
              </div>

              <div className="flex justify-between">
                <Button variant="ghost" size="sm" onClick={() => setMonth(addMonths(month, -1))}>
                  ← Earlier
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setMonth(addMonths(month, 1))}>
                  Later →
                </Button>
              </div>

              <div className="flex flex-col gap-3 border-t border-border-raised pt-4">
                <Bound
                  label="Start"
                  date={fromDate}
                  time={fromTime}
                  onDate={setFromDate}
                  onTime={setFromTime}
                  shortcut="Start of day"
                  onShortcut={() => setFromTime('00:00:00')}
                />
                <Bound
                  label="End"
                  date={toDate}
                  time={toTime}
                  onDate={setToDate}
                  onTime={setToTime}
                  shortcut="End of day"
                  onShortcut={() => setToTime('23:59:59')}
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 border-t border-border-raised px-3 py-3">
            <button
              type="button"
              onClick={() => applyRelative(DEFAULT_MINUTES)}
              className="text-meta font-medium text-muted hover:text-primary"
            >
              Reset to 24 hours
            </button>
            {tab === 'absolute' && start && end && !ordered && (
              <span className="text-meta text-danger">Start must be before end</span>
            )}
            {tab === 'absolute' && ordered && tooWide && maxMinutes !== undefined && (
              <span className="text-meta text-danger">
                At most {relativeLabel(maxMinutes).replace('Last ', '')} at once
              </span>
            )}
            <div className="ml-auto flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              {tab === 'absolute' && (
                <Button
                  size="sm"
                  disabled={!ordered || tooWide}
                  onClick={() => {
                    if (!start || !end) return;
                    onChange({
                      ...filters,
                      minutes: undefined,
                      from: start.toISOString(),
                      to: end.toISOString(),
                    });
                    setOpen(false);
                  }}
                >
                  Apply
                </Button>
              )}
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

const Bound = ({
  label,
  date,
  time,
  onDate,
  onTime,
  shortcut,
  onShortcut,
}: {
  label: string;
  date: string;
  time: string;
  onDate: (value: string) => void;
  onTime: (value: string) => void;
  shortcut: string;
  onShortcut: () => void;
}) => (
  <div className="flex flex-col gap-label">
    <span className="text-micro text-muted">{label}</span>
    <div className="flex gap-2">
      <input
        value={date}
        onChange={(event) => onDate(event.target.value)}
        placeholder="2026/09/10"
        aria-label={`${label} date`}
        className="h-control-xs min-w-0 flex-1 rounded-control border border-border-raised bg-field px-inset text-meta text-primary outline-none focus:border-border-focus"
      />
      <div className="relative min-w-0 flex-1">
        <input
          value={time}
          onChange={(event) => onTime(event.target.value)}
          placeholder="00:00:00"
          aria-label={`${label} time`}
          className="h-control-xs w-full rounded-control border border-border-raised bg-field px-inset pr-20 font-mono text-meta text-primary outline-none focus:border-border-focus"
        />
        <button
          type="button"
          onClick={onShortcut}
          className="absolute top-1/2 right-2 -translate-y-1/2 text-micro whitespace-nowrap text-placeholder hover:text-primary"
        >
          {shortcut}
        </button>
      </div>
    </div>
  </div>
);

const relativeLabel = (minutes: number): string => {
  if (minutes < 60) return `Last ${minutes} minutes`;
  if (minutes < 60 * 24) {
    const hours = Math.round(minutes / 60);
    return `Last ${hours} hour${hours === 1 ? '' : 's'}`;
  }
  const days = Math.round(minutes / (60 * 24));
  if (days < 60) return `Last ${days} day${days === 1 ? '' : 's'}`;
  return `Last ${Math.round(days / 30)} months`;
};
